import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from './theme';

// Container
export const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
`;

// Typography
export const H1 = styled.h1`
  font-weight: ${theme.typography.h1.fontWeight};
  font-size: ${theme.typography.h1.fontSize};
  line-height: ${theme.typography.h1.lineHeight};
  color: ${theme.colors.text};
`;

export const Hxl = styled.h1`
  font-weight: ${theme.typography.hxl.fontWeight};
  font-size: ${theme.typography.hxl.fontSize};
  line-height: ${theme.typography.hxl.lineHeight};
  color: ${theme.colors.text};
`;

export const H3 = styled.h3`
  font-size: ${theme.typography.h3.fontSize};
  font-weight: ${theme.typography.h3.fontWeight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const GradientTitle = styled(H3)`
  background: linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accent2}, ${theme.colors.white});
  -webkit-background-clip: text;
  color: transparent;
`;

export const TextMuted = styled.p`
  color: ${theme.colors.muted};
  margin: 0;
`;

// Buttons - Self-contained styled components, NO global impact
export const Button = styled.button`
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.card};
  color: ${theme.colors.text};
  padding: 0.6rem 0.9rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  font: inherit;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonPrimary = styled(Button)`
  background: linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accent2});
  color: #001018;
  border: none;

  &:hover {
    opacity: 0.95;
  }
`;

export const ButtonLink = styled(Link)`
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.card};
  color: ${theme.colors.text};
  padding: 0.6rem 0.9rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

export const ButtonLinkPrimary = styled(Link)`
  background: linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accent2});
  color: #001018;
  border: none;
  padding: 0.6rem 0.9rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.95;
  }
`;

// Cards
export const Card = styled.div`
  background: ${theme.colors.card};
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${({ padding }) => padding || '1rem'};
`;

export const Card2 = styled(Card)`
  background: ${theme.colors.card2};
`;

// Layout
export const Flex = styled.div`
  display: flex;
  align-items: ${({ align }) => align || 'flex-start'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  gap: ${({ gap }) => gap || '0.5rem'};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr'};
  gap: ${({ gap }) => gap || '1rem'};
`;

// Auto Grid
export const AutoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

export const AutoGridSm = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

// Responsive Grids
export const Grid2 = styled(Grid)`
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

export const Grid3 = styled(Grid)`
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${theme.breakpoints.xl}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const Grid4 = styled(Grid)`
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: ${theme.breakpoints.xl}) {
    grid-template-columns: 1fr 1fr;
  }
`;

// Chip/Badge
export const Chip = styled.span`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  padding: 4px 10px;
  font-size: 12px;
  display: inline-block;
  color: ${theme.colors.text};
`;

// Form Inputs
export const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.card};
  color: ${theme.colors.text};
  outline: none;
  font: inherit;

  &:focus {
    border-color: ${theme.colors.accent};
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.card};
  color: ${theme.colors.text};
  outline: none;
  font: inherit;

  &:focus {
    border-color: ${theme.colors.accent};
  }
`;

// Spacing Utilities
export const Spacing = {
  pt16: styled.div`
    padding-top: 4rem;
  `,
  pb6: styled.div`
    padding-bottom: 1.5rem;
  `,
  mt1: styled.div`
    margin-top: 0.25rem;
  `,
  mt2: styled.div`
    margin-top: 0.5rem;
  `,
  mt3: styled.div`
    margin-top: 0.75rem;
  `,
  mt4: styled.div`
    margin-top: 1rem;
  `,
  mt5: styled.div`
    margin-top: 1.25rem;
  `,
  mt6: styled.div`
    margin-top: 1.5rem;
  `,
  mt7: styled.div`
    margin-top: 1.75rem;
  `,
};

// Text utilities
export const TextCenter = styled.div`
  text-align: center;
`;

export const FontBold = styled.span`
  font-weight: 700;
`;

